import { platformScreens } from '@/mocks/mt4mt5Page';

type HeroProps = {
  mt4Lessons: number;
  mt5Lessons: number;
};

export default function Hero({ mt4Lessons, mt5Lessons }: HeroProps) {
  return (
    <section className="relative overflow-hidden border-b border-primary-100 bg-primary-50">
      <div className="pointer-events-none absolute inset-0 grid-lines opacity-40" />
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-primary-100/70 blur-3xl" />
      <div className="pointer-events-none absolute -right-20 -top-16 h-80 w-80 rounded-full bg-accent-100/60 blur-3xl" />

      <div className="relative mx-auto w-full max-w-7xl px-4 pb-14 pt-6 md:px-6 md:pb-20 md:pt-8 lg:px-10">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[1.02fr_1fr] lg:gap-12">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-background-50 px-3 py-1.5 text-xs font-semibold text-primary-700">
              <i className="ri-graduation-cap-line text-sm" />
              ศูนย์การเรียนรู้แพลตฟอร์มเทรด
            </span>

            <h1 className="mt-5 text-balance font-heading text-3xl font-semibold leading-[1.15] text-foreground-950 sm:text-4xl lg:text-[52px]">
              คู่มือ MT4 และ MT5
              <span className="block text-primary-600">สำหรับมือใหม่</span>
            </h1>

            <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-700 md:text-base">
              เรียนรู้วิธีใช้งาน MetaTrader 4 และ MetaTrader 5 ตั้งแต่การติดตั้ง การใช้งานกราฟ อินดิเคเตอร์
              ไปจนถึงการจัดการคำสั่งซื้อขาย พร้อมคู่มือสำหรับผู้เริ่มต้น
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <a
                href="#mt4-guide"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-circle-line text-lg" />
                เริ่มเรียนรู้ MT4
              </a>
              <a
                href="#mt5-guide"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-5 py-3 text-sm font-semibold text-primary-700 transition-colors hover:border-primary-500 hover:bg-primary-100/60"
              >
                <i className="ri-play-circle-line text-lg" />
                เริ่มเรียนรู้ MT5
              </a>
            </div>

            <dl className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-foreground-600">
              <div className="flex items-center gap-2">
                <dt className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-file-list-3-line text-sm" />
                </dt>
                <dd>
                  <span className="font-num font-semibold text-foreground-900">{mt4Lessons}</span> บทความ MT4
                </dd>
              </div>
              <div className="flex items-center gap-2">
                <dt className="flex h-6 w-6 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-file-list-3-line text-sm" />
                </dt>
                <dd>
                  <span className="font-num font-semibold text-foreground-900">{mt5Lessons}</span> บทความ MT5
                </dd>
              </div>
              <div className="flex items-center gap-2">
                <dt className="flex h-6 w-6 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                  <i className="ri-translate-2 text-sm" />
                </dt>
                <dd>เนื้อหาภาษาไทยทั้งหมด</dd>
              </div>
            </dl>
          </div>

          <div className="relative mx-auto h-[300px] w-full max-w-[520px] sm:h-[360px] lg:h-[420px] lg:max-w-none">
            <figure className="absolute right-0 top-0 w-[72%] rotate-[2deg] rounded-xl border border-background-200 bg-background-50 p-2.5">
              <div className="flex items-center justify-between px-1 pb-2">
                <span className="rounded-full bg-accent-100 px-2.5 py-0.5 text-[11px] font-semibold text-accent-800">
                  MT5
                </span>
                <span className="text-[10px] text-foreground-400">MetaTrader 5</span>
              </div>
              <div className="h-[150px] w-full overflow-hidden rounded-lg bg-background-100 sm:h-[185px] lg:h-[215px]">
                <img
                  src={platformScreens.mt5.image}
                  alt="ภาพประกอบหน้าจอ MetaTrader 5 แบบหลายแผงกราฟ สำหรับการเรียนรู้การใช้งาน MT5"
                  title="คู่มือ MT5 สำหรับมือใหม่ - Forex Academy"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </figure>

            <figure className="absolute bottom-0 left-0 w-[70%] -rotate-[2deg] rounded-xl border border-primary-200 bg-background-50 p-2.5">
              <div className="flex items-center justify-between px-1 pb-2">
                <span className="rounded-full bg-primary-500 px-2.5 py-0.5 text-[11px] font-semibold text-background-50">
                  MT4
                </span>
                <span className="text-[10px] text-foreground-400">MetaTrader 4</span>
              </div>
              <div className="h-[150px] w-full overflow-hidden rounded-lg bg-background-100 sm:h-[185px] lg:h-[215px]">
                <img
                  src={platformScreens.mt4.image}
                  alt="ภาพประกอบหน้าจอ MetaTrader 4 พร้อมกราฟแท่งเทียน สำหรับการเรียนรู้การใช้งาน MT4"
                  title="คู่มือ MT4 สำหรับมือใหม่ - Forex Academy"
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <figcaption className="px-1 pt-1.5 text-[10px] text-foreground-500">
                ภาพประกอบหน้าจอเพื่อการเรียนรู้
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
}